import { createSlice, CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import { buildSubStateSelector } from '../../store/utilities/useAppStateSelector';
import { FeedPost, FeedCategory, FeedSource, FeedFilters, FeedListResponse } from '../../models/feed';

const initialState = {
    // News state
    newsPosts: [] as FeedPost[],
    newsLoading: false,
    newsPage: 1,
    newsTotalPages: 1,

    // Events state
    eventsPosts: [] as FeedPost[],
    eventsLoading: false,
    eventsPage: 1,
    eventsTotalPages: 1,

    // Detail view
    currentPost: null as FeedPost | null,
    relatedPosts: [] as FeedPost[],
    detailLoading: false,

    // Filters & metadata
    categories: [] as FeedCategory[],
    sources: [] as FeedSource[],
    activeFilters: {} as FeedFilters,
};

export type FeedAppState = typeof initialState;

export const slice = createSlice<FeedAppState, {
    // News actions
    setNewsPosts: CaseReducer<FeedAppState, PayloadAction<FeedListResponse>>;
    setNewsLoading: CaseReducer<FeedAppState, PayloadAction<boolean>>;
    setNewsPage: CaseReducer<FeedAppState, PayloadAction<number>>;

    // Events actions
    setEventsPosts: CaseReducer<FeedAppState, PayloadAction<FeedListResponse>>;
    setEventsLoading: CaseReducer<FeedAppState, PayloadAction<boolean>>;
    setEventsPage: CaseReducer<FeedAppState, PayloadAction<number>>;

    // Detail actions
    setCurrentPost: CaseReducer<FeedAppState, PayloadAction<FeedPost | null>>;
    setRelatedPosts: CaseReducer<FeedAppState, PayloadAction<FeedPost[]>>;
    setDetailLoading: CaseReducer<FeedAppState, PayloadAction<boolean>>;

    // Metadata actions
    setCategories: CaseReducer<FeedAppState, PayloadAction<FeedCategory[]>>;
    setSources: CaseReducer<FeedAppState, PayloadAction<FeedSource[]>>;
    setActiveFilters: CaseReducer<FeedAppState, PayloadAction<FeedFilters>>;
    clearActiveFilters: CaseReducer<FeedAppState, PayloadAction<void>>;
}>({
    name: 'feed',
    initialState,
    reducers: {
        setNewsPosts: (state, { payload }) => {
            state.newsPosts = payload.posts;
            state.newsPage = payload.page;
            state.newsTotalPages = payload.totalPages;
        },
        setNewsLoading: (state, { payload }) => {
            state.newsLoading = payload;
        },
        setNewsPage: (state, { payload }) => {
            state.newsPage = payload;
        },

        setEventsPosts: (state, { payload }) => {
            state.eventsPosts = payload.posts;
            state.eventsPage = payload.page;
            state.eventsTotalPages = payload.totalPages;
        },
        setEventsLoading: (state, { payload }) => {
            state.eventsLoading = payload;
        },
        setEventsPage: (state, { payload }) => {
            state.eventsPage = payload;
        },

        setCurrentPost: (state, { payload }) => {
            state.currentPost = payload;
        },
        setRelatedPosts: (state, { payload }) => {
            state.relatedPosts = payload;
        },
        setDetailLoading: (state, { payload }) => {
            state.detailLoading = payload;
        },

        setCategories: (state, { payload }) => {
            state.categories = payload;
        },
        setSources: (state, { payload }) => {
            state.sources = payload;
        },
        setActiveFilters: (state, { payload }) => {
            state.activeFilters = { ...state.activeFilters, ...payload };
        },
        clearActiveFilters: (state) => {
            state.activeFilters = {};
        },
    },
});

export const useAppStateFeedSelector = buildSubStateSelector<FeedAppState>(
    (state) => state.feed
);

export const feedActions = slice.actions;
export const feedReducer = slice.reducer;
